/**
 * Shared class sets for the service desk (EP-27 FE-3), on the theme tokens:
 * the slate ramp, `accent` for primary and focus, `panel` for raised surfaces.
 * Every set has its dark counterpart, and every interactive set a visible
 * focus ring (HS design standard: light-and-dark, inclusive-by-default).
 */
import { useEffect, useState } from 'react';
import { resourceApi } from '../../services/api';

export const ring =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 '
  + 'ring-offset-white dark:ring-offset-slate-900';

export const field =
  'w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 '
  + 'bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 '
  + 'placeholder:text-slate-500 dark:placeholder:text-slate-400 '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent';

export const compactField =
  'px-2 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 '
  + 'bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent';

export const label = 'block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1';
export const help = 'mt-1 text-xs text-slate-600 dark:text-slate-400';
export const errorText = 'mt-1 text-xs text-red-600 dark:text-red-400';
export const warnText = 'text-xs text-amber-700 dark:text-amber-400';
export const muted = 'text-slate-600 dark:text-slate-400';
export const heading = 'text-xs font-semibold text-slate-700 dark:text-slate-300';

export const primaryButton =
  `px-3 py-1.5 text-sm rounded-lg bg-accent text-white hover:bg-accent-hover disabled:opacity-50 ${ring}`;
export const secondaryButton =
  'px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 '
  + 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 '
  + `disabled:opacity-50 ${ring}`;
export const ghostButton =
  'text-xs text-accent dark:text-accent-light hover:underline rounded ' + ring;

export interface AssigneeOption { email: string; name: string }

/**
 * People a ticket can be assigned to: the resource directory when the caller
 * may read it, plus whoever already holds tickets on this board. The field is
 * an email input either way, so a person not in either list can still be named.
 */
export function useAssigneeOptions(known: string[] = []): AssigneeOption[] {
  const [resources, setResources] = useState<AssigneeOption[]>([]);
  useEffect(() => {
    let live = true;
    resourceApi.list(true)
      .then((rows) => {
        if (!live) return;
        setResources((rows || [])
          .filter((r: { resourceType?: string; email?: string }) =>
            (r.resourceType || 'user') === 'user' && !!r.email)
          .map((r: { email: string; name?: string }) => ({ email: r.email, name: r.name || r.email })));
      })
      .catch(() => { /* not everyone may read the directory; known assignees still show */ });
    return () => { live = false; };
  }, []);
  const seen = new Set(resources.map((r) => r.email.toLowerCase()));
  return [
    ...resources,
    ...known.filter((e) => !seen.has(e.toLowerCase())).map((e) => ({ email: e, name: e })),
  ];
}

/** "3 h", "2 d": how long ago a ticket was raised, for the board cards. */
export function ageLabel(iso?: string, now = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((now - t) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}
